<template>
	<CookieTemplateLayout
		:header-title="'Appearance Templates' | useLabels('Appearance Templates')"
		:header-caption="'Add, edit and manage appearance templates' | useLabels('Add, edit and manage appearance templates')"
	>
		<template #content>
			<FilterBar
				:search-query.sync="cookieAppearanceTemplateSearchQuery"
				search-query-label="Appearance Template Name"
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				@persistSearchQuery="changeCookieAppearanceTemplateSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
			>
				<template #action>
					<!-- add permission -->
					<PrimaryActionButton
						v-if="userCanCreateUpdate"
						@click="onCreateClick()"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Create
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<SectionCard>
				<template #title>
					Appearance Templates
				</template>
				<template #body>
					<DataTable
						:headers="tableHeaders"
						:items="filteredCookieAppearanceTemplates"
						sort-by="id"
						:sort-desc="true"
						@click:row="onEditClick"
					>
						<template #item.name="{ item }">
							{{ item.name }}
							<Chip
								v-if="item.isSystem"
								class="ml-2 prussian-blue white--text"
							>
								Cassie Template
							</Chip>
						</template>
						<template #item.action="{ item }">
							<v-menu
								offset-y
								left
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="!item.isSystem && userCanCreateUpdate && userHasBrand(item.brandId)"
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Edit
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-else
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												View
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="(userCanCreateUpdate && userHasBrand(item.brandId)) || item.isSystem"
										@click.stop.prevent="onCloneClick(item, true)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Clone
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanDelete && item.usage === 0 && !item.isSystem && userHasBrand(item.brandId)"
										@click.stop.prevent="cookieAppearanceTemplateToDelete = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Delete
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</template>
			</SectionCard>
			<ConfirmDeleteModal
				v-if="cookieAppearanceTemplateToDelete && userCanDelete"
				:entity-name="cookieAppearanceTemplateToDelete.name"
				entity-type="Appearance Template"
				@close="cookieAppearanceTemplateToDelete = null"
				@delete="deleteCookieAppearanceTemplate"
			/>
		</template>
	</CookieTemplateLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import CookieTemplateLayout from '../cookie-template-layout.vue'
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import Chip from '../../../../../../../shared/components/chip.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { MANAGE_COOKIE_APPEARANCE_TEMPLATE } from '../../../../../router/route-names.js'
import { getCookieAppearanceTemplates, deleteCookieAppearanceTemplate } from '../../../../../../../shared/utils/api/cookies/cookie-appearance-templates.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { CAN_CREATE_UPDATE_COOKIE_APPEARANCE_TEMPLATES, CAN_DELETE_COOKIE_APPEARANCE_TEMPLATES } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { changeCookieAppearanceTemplateSearchQuery, cookieAppearanceTemplateSearchQuery } from '../../../../../../../shared/state/cookie-appearance-templates.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../../../shared/state/brands.js'
export default {
	components: {
		CookieTemplateLayout,
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		ConfirmDeleteModal,
		DataTable,
		Chip
	},
	setup () {
		return {
			userHasBrand,
			changeCookieAppearanceTemplateSearchQuery,
			cookieAppearanceTemplateSearchQuery,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId
		}
	},
	data () {
		return {
			cookieAppearanceTemplates: [],
			cookieAppearanceTemplateToDelete: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_APPEARANCE_TEMPLATES)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_COOKIE_APPEARANCE_TEMPLATES)
		},
		tableHeaders () {
			return [
				{ value: 'id', text: 'ID', width: '8%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '12%' },
				{ value: 'name', text: 'Appearance Template Name', width: '50%' },
				{ value: 'usage', text: 'Usage', width: '10%' },
				{ value: 'createdDate', text: 'Created Date', width: '12%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredCookieAppearanceTemplates () {
			return this.cookieAppearanceTemplates.filter(({ name, brandId }) => {
				let check = true
				if (cookieAppearanceTemplateSearchQuery.value) check = name.toLowerCase().includes(cookieAppearanceTemplateSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				return check
			}).map(appearanceTemplate => ({
				...appearanceTemplate
			}))
		},
		shouldShowChips () {
			return item => {
				return item.forceBannerInteraction ||
						!item.preBanner?.preBannerEnabled ||
						item.gpcBanner?.gpcBannerEnabled ||
						item.banner?.autoShowModal
			}
		}
	},
	created () {
		this.getCookieAppearanceTemplates()
	},
	methods: {
		onCreateClick () {
			this.$router.push({
				name: MANAGE_COOKIE_APPEARANCE_TEMPLATE
			})
		},
		onEditClick (item) {
			this.$router.push({
				name: MANAGE_COOKIE_APPEARANCE_TEMPLATE,
				params: {
					cookieAppearanceTemplateToEdit: item
				}
			})
		},
		onCloneClick (item, cloneMode) {
			const cookieAppearanceTemplateToEdit = JSON.parse(JSON.stringify(item))
			cookieAppearanceTemplateToEdit.name = cookieAppearanceTemplateToEdit.name + ' (clone)'
			this.$router.push({
				name: MANAGE_COOKIE_APPEARANCE_TEMPLATE,
				params: {
					cookieAppearanceTemplateToEdit,
					cloneMode: cloneMode
				}
			})
		},
		async getCookieAppearanceTemplates () {
			const { cookieAppearanceTemplates } = await getCookieAppearanceTemplates()
			this.cookieAppearanceTemplates = cookieAppearanceTemplates
		},
		async deleteCookieAppearanceTemplate () {
			await deleteCookieAppearanceTemplate(this.cookieAppearanceTemplateToDelete.id)
			showSnackbar('You have removed this appearance template')
			this.cookieAppearanceTemplateToDelete = null
			this.getCookieAppearanceTemplates()
		}
	}
}

</script>
